import { Check, GitFork, Globe, Lock, X } from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Dialog,
  DialogDisclosure,
  DialogDismiss,
  DialogHeading,
  DialogProvider,
  IconButton,
  Spinner,
  TextField,
} from "@replicate/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useDebounce } from "../hooks";
import type { AccountWithAvatar, Model } from "../types/models";
import { route } from "../urls";
import { getStaticAssetPath } from "../util";

interface ForkModelResponse {
  owner: string;
  name: string;
}

async function forkModel({
  model,
  name,
  visibility,
}: {
  model: Model;
  name: string;
  visibility: "private" | "public";
}): Promise<ForkModelResponse> {
  const response = await fetch(
    route("api_model_fork", {
      username: model.owner,
      name: model.name,
    }),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": Cookies.get("csrftoken") ?? "",
      },
      body: JSON.stringify({ name, visibility }),
    }
  );

  if (response.ok) {
    return await response.json();
  }

  if (response.status === 400) {
    const { detail } = await response.json<{ detail: string }>();
    throw new Error(detail);
  }

  throw new Error("Failed to fork model");
}

async function checkModelAvailability({
  name,
  username,
}: { name: string; username: string }): Promise<{
  available: boolean;
  message?: string;
}> {
  try {
    const response = await fetch(
      route("api_model_available", {
        username,
        name,
      }),
      {
        headers: {
          "X-CSRFToken": Cookies.get("csrftoken") ?? "",
        },
      }
    );

    if (response.ok) {
      const { available } = await response.json<{
        name: string;
        available: true;
      }>();
      return { available };
    }

    if (response.status === 404) {
      return { available: false, message: "Please enter a valid model name." };
    }

    let message: string | undefined;
    try {
      const { detail } = await response.json<{ detail: string }>();
      message = detail;
    } catch {}

    return { available: false, message };
  } catch {
    return { available: false };
  }
}

export default function ForkModelDialog({
  model,
  account,
  disclosure = (
    <Button
      startIcon={<GitFork />}
      variant="outlined"
      className="w-full relative"
    />
  ),
}: {
  model: Model;
  account: AccountWithAvatar;
  disclosure?: React.ReactElement;
}) {
  const [name, setName] = useState(() => {
    // If you own the model, we want to pre-fill the input
    // with the name + "1"
    if (model.owner === account.username) {
      const separator = model.name.includes("_") ? "_" : "-";

      return `${model.name}${separator}1`;
    }
    // Otherwise, let's pre-fill the input with the original model name.
    return model.name;
  });
  const [visibility, setVisibility] = useState<"private" | "public">("private");
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);

  const debouncedName = useDebounce(name, 300);

  const {
    mutate,
    status: mutationStatus,
    error: mutationError,
  } = useMutation({
    mutationFn: forkModel,
    onSuccess: (data) => {
      setOpen(false);
      window.location.href = `/editor/${data.owner}/${data.name}`;
    },
    onError: (error) => {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error("Failed to fork model");
      }
    },
  });

  const { data, isFetching, isPending, isSuccess } = useQuery({
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: Boolean(debouncedName),
    queryKey: ["model-exists", debouncedName],
    queryFn: () =>
      checkModelAvailability({
        name: debouncedName,
        username: account.username,
      }),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name) {
      return;
    }
    if (!visibility) {
      return;
    }
    mutate({ model, name, visibility });
  };

  const fallbackAvatarUrl =
    account.kind === "organization"
      ? getStaticAssetPath("/static/placeholder-avatar-organization.svg")
      : getStaticAssetPath("/static/placeholder-avatar-user.svg");

  const helpText = useMemo(() => {
    if (isPending) {
      return (
        <p className="leading-relaxed text-r8-sm text-r8-gray-11">
          Pick a short and memorable name, like <code>my-custom-model</code>.
          You can use lowercase characters and dashes.
        </p>
      );
    }

    if (isSuccess) {
      if (!data.available) {
        const fallbackMessage =
          "A model with this name already exists. Please choose a different name.";
        return (
          <p className="leading-relaxed text-r8-sm text-r8-gray-11">
            {data.message || fallbackMessage}
          </p>
        );
      }
      return (
        <p className="leading-relaxed text-r8-sm text-r8-gray-11">
          Model name <strong>{debouncedName}</strong> is available! When you
          click the "Fork model" button, a copy of it will be created under your
          account and you will be redirected to the editor where you can modify
          and run your new model.
        </p>
      );
    }
  }, [isPending, isSuccess, data, debouncedName]);

  const severity = useMemo(() => {
    if (isSuccess && data.available) return "success";
    if (isSuccess && !data.available) return "error";
    return undefined;
  }, [data, isSuccess]);

  return (
    <DialogProvider
      open={open}
      setOpen={(open) => {
        setOpen(open);
        if (!open) {
          setName("");
          setVisibility("private");
        }
      }}
      setMounted={setMounted}
    >
      <DialogDisclosure render={disclosure}>
        <span className="whitespace-nowrap">Fork model</span>
        <Badge rounded variant="danger" size="sm">
          New
        </Badge>
      </DialogDisclosure>
      {mounted ? (
        <Dialog size="xl" portal className="z-50">
          <div className="p-4 flex items-center justify-between">
            <DialogHeading>Fork model</DialogHeading>
            <DialogDismiss render={<IconButton size="sm" variant="clear" />}>
              <X />
            </DialogDismiss>
          </div>
          <div className="px-4 pb-4 flex flex-col gap-6">
            <div>
              <p className="text-r8-sm">
                Forking{" "}
                <span className="font-semibold">
                  {model.owner}/{model.name}
                </span>{" "}
                will create a copy of it under your account. You can then modify
                it, run it, and share it with others. Give the forked model a
                name:
              </p>
            </div>

            <form
              className="flex flex-col gap-5"
              id="fork-model-form"
              onSubmit={handleSubmit}
            >
              <div className="flex gap-3">
                <div className="flex-1">
                  <TextField
                    label="Owner"
                    readOnly
                    id="owner"
                    startAdornment={
                      <div className="size-6">
                        <img
                          className={`shrink-0 ${
                            account.kind === "user" ? "rounded-full" : ""
                          } w-full h-full object-cover`}
                          src={account.avatar_url ?? fallbackAvatarUrl}
                        />
                      </div>
                    }
                    value={account.username}
                  />
                </div>
                <div className="flex-1">
                  <TextField
                    id="name"
                    required
                    label="Name"
                    name="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    endAdornment={
                      <>
                        {isFetching ? (
                          <div className="size-4">
                            <Spinner />
                          </div>
                        ) : null}
                        {isSuccess && data.available ? (
                          <Check weight="bold" className="text-r8-green-10" />
                        ) : null}
                      </>
                    }
                    severity={severity}
                    pattern="^[a-z0-9-]+$"
                    placeholder="my-custom-model"
                  />
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label
                    className="flex items-center justify-between cursor-pointer"
                    htmlFor="visibility-private"
                  >
                    <div className="flex items-center gap-4">
                      <Lock className="text-r8-gray-11 shrink-0" size={24} />
                      <p className="flex flex-col gap-0.5 text-r8-sm">
                        <span className="font-semibold">Private</span>
                        <span className="text-r8-gray-11">
                          Only you can run this model and see its source code.
                        </span>
                      </p>
                    </div>
                    <input
                      type="radio"
                      id="visibility-private"
                      name="visibility"
                      checked={visibility === "private"}
                      onChange={() => setVisibility("private")}
                      value="private"
                    />
                  </label>
                </div>
                <div>
                  <label
                    className="flex items-center justify-between cursor-pointer"
                    htmlFor="visibility-public"
                  >
                    <div className="flex items-center gap-4">
                      <Globe className="text-r8-gray-11 shrink-0" size={24} />
                      <p className="flex flex-col gap-0.5 text-r8-sm">
                        <span className="font-semibold">Public</span>
                        <span className="text-r8-gray-11">
                          Anyone can run this model and see its source code.
                        </span>
                      </p>
                    </div>
                    <input
                      type="radio"
                      id="visibility-public"
                      name="visibility"
                      checked={visibility === "public"}
                      onChange={() => setVisibility("public")}
                      value="public"
                    />
                  </label>
                </div>
              </div>
              <div className="min-h-20">{helpText}</div>
            </form>

            {mutationError ? (
              <div className="transform translate-y-px">
                <div className="bg-r8-red-3 border border-r8-red-7 text-r8-red-11 px-3 py-2 rounded text-r8-sm">
                  {mutationError instanceof Error
                    ? mutationError.message
                    : "Failed to fork model"}
                </div>
              </div>
            ) : null}
          </div>
          <div className="p-4 flex justify-end border-t border-r8-gray-a4">
            <Button
              disabled={
                !model || !name || isPending || (isSuccess && !data.available)
              }
              loading={mutationStatus === "pending"}
              form="fork-model-form"
              size="sm"
              type="submit"
            >
              Fork model
            </Button>
          </div>
        </Dialog>
      ) : null}
    </DialogProvider>
  );
}
